/**
 * Language-specific display labels for section kinds + response fields.
 * Mirrors functions/_shared/sectionLabels.ts for the SPA.
 */

export const SECTION_KIND_KEYS = [
  "first",
  "second",
  "psalm",
  "acclamation",
  "gospel",
  "other",
] as const;

export type SectionKindKey = (typeof SECTION_KIND_KEYS)[number];

export const RESPONSE_LABEL_KEYS = ["psalmResponse", "acclamationResponse"] as const;
export type ResponseLabelKey = (typeof RESPONSE_LABEL_KEYS)[number];

export type SectionLabels = Record<SectionKindKey | ResponseLabelKey, string>;

export const DEFAULT_SECTION_LABELS: Record<string, SectionLabels> = {
  en: {
    first: "Reading I",
    second: "Reading II",
    psalm: "Responsorial Psalm",
    acclamation: "Alleluia",
    gospel: "Gospel",
    other: "Reading",
    psalmResponse: "Response",
    acclamationResponse: "Alleluia",
  },
  tl: {
    first: "Unang Pagbasa",
    second: "Ikalawang Pagbasa",
    psalm: "Salmong Tugunan",
    acclamation: "Aleluya",
    gospel: "Mabuting Balita",
    other: "Pagbasa",
    psalmResponse: "Tugon",
    acclamationResponse: "Aleluya",
  },
};

export const LABEL_FIELD_META: Array<{ key: SectionKindKey | ResponseLabelKey; hint: string }> = [
  { key: "first", hint: "First reading" },
  { key: "second", hint: "Second reading" },
  { key: "psalm", hint: "Psalm section" },
  { key: "acclamation", hint: "Gospel acclamation section" },
  { key: "gospel", hint: "Gospel" },
  { key: "other", hint: "Fallback" },
  { key: "psalmResponse", hint: "Shared psalm refrain field" },
  { key: "acclamationResponse", hint: "Alleluia / Aleluya refrain field" },
];

export function defaultSectionLabels(languageCode: string): SectionLabels {
  return {
    ...(DEFAULT_SECTION_LABELS[languageCode] ?? DEFAULT_SECTION_LABELS.en!),
  };
}

export function parseSectionLabels(
  raw: string | null | undefined,
  languageCode: string,
): SectionLabels {
  const base = defaultSectionLabels(languageCode);
  if (!raw?.trim()) return base;
  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const out = { ...base };
    for (const key of [...SECTION_KIND_KEYS, ...RESPONSE_LABEL_KEYS]) {
      const value = parsed[key];
      if (typeof value === "string" && value.trim()) out[key] = value.trim();
    }
    return out;
  } catch {
    return base;
  }
}

export function sectionKindLabel(labels: SectionLabels, kind: string): string {
  if (kind in labels) return labels[kind as keyof SectionLabels];
  return labels.other;
}

export function responseFieldLabelFromConfig(labels: SectionLabels, kind: string): string {
  if (kind === "acclamation") return labels.acclamationResponse;
  if (kind === "psalm") return labels.psalmResponse;
  return labels.psalmResponse;
}
